// @flow
import { useCallback, useEffect, useRef, useState } from 'react';

const SAVE_DELAY = 800;
const MAX_LENGTH = 100;

export type ObservationSaveStatus = 'idle' | 'pending' | 'saved' | 'error';

// Saves a line item observation a moment after the user stops typing or leaves
// the field, so there is no save button. `save` must resolve to true on success.
const useObservationAutosave = (
  initialObservation: ?string,
  save: (observation: string) => Promise<boolean>,
) => {
  const [observation, setObservation] = useState(initialObservation || '');
  const [isObservationOpen, setIsObservationOpen] = useState(
    !!initialObservation,
  );
  const [badObservation, setBadObservation] = useState('');
  const [observationStatus, setObservationStatus] = useState(
    initialObservation ? 'saved' : 'idle',
  );

  const timeoutRef = useRef(null);
  const isPendingRef = useRef(false);
  const isMountedRef = useRef(true);
  // Delayed saves must use the values from the latest render
  const latestRef = useRef();
  latestRef.current = { observation, save };

  const saveObservation = useCallback(async () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    if (!isPendingRef.current) return;
    isPendingRef.current = false;

    const { observation: sentObservation, save: saveLatest } =
      latestRef.current;
    const saved = await saveLatest(sentObservation);

    // The user kept typing, so this result is outdated
    if (
      !isMountedRef.current ||
      latestRef.current.observation !== sentObservation
    ) {
      return;
    }
    setObservationStatus(saved ? 'saved' : 'error');
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Leaving the cart (e.g. to checkout) sends the pending text instead of dropping it
      if (timeoutRef.current) saveObservation();
    };
  }, [saveObservation]);

  const handleChangeObservation = e => {
    const value = e.target.value;
    if (value.length >= MAX_LENGTH) return;

    setObservation(value);
    setBadObservation(
      value.length === MAX_LENGTH - 1 ? 'Observação muito longa' : '',
    );
    setObservationStatus('pending');
    isPendingRef.current = true;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(saveObservation, SAVE_DELAY);
  };

  const retryObservationSave = () => {
    setObservationStatus('pending');
    isPendingRef.current = true;
    saveObservation();
  };

  const handleToggleObservation = (value: boolean) => {
    setIsObservationOpen(value);
    if (!value) saveObservation();
  };

  return {
    observation,
    isObservationOpen,
    badObservation,
    observationStatus,
    handleChangeObservation,
    handleToggleObservation,
    saveObservation,
    retryObservationSave,
  };
};

export default useObservationAutosave;
